/**
 * XRPWAHome — v10.9.0
 * Landing / home de la PWA XR (ruta `/xr`, scope PWA).
 * Detecta capacidades del dispositivo, muestra estado de instalación,
 * y permite abrir el escáner de QR para vincular una sesión activa.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BrowserMultiFormatReader } from '@zxing/browser';
import useXRCapabilities from '../../xr/hooks/useXRCapabilities';
import XRInstallOnboarding, { shouldShowXRInstallOnboarding } from '../../xr/components/XRInstallOnboarding';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Cube, Camera, QrCode, CheckCircle, WarningCircle, DeviceMobile, CellSignalFull, CellSignalSlash, ShieldCheck, DownloadSimple } from '@phosphor-icons/react';

const CAP_META = [
  { key: 'camera_supported',           label: 'Cámara',            required: true },
  { key: 'marker_tracking_supported',  label: 'Marcadores QR',     required: true },
  { key: 'webxr_supported',            label: 'WebXR immersive-ar', required: false },
  { key: 'depth_supported',            label: 'Depth API',          required: false },
  { key: 'spatial_anchor_supported',   label: 'Spatial Anchors',    required: false },
  { key: 'voice_supported',            label: 'Voz (TTS/STT)',      required: false },
];

export default function XRPWAHome() {
  const caps = useXRCapabilities();
  const navigate = useNavigate();

  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [manualToken, setManualToken] = useState('');
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const videoRef = useRef(null);
  const readerRef = useRef(null);

  useEffect(() => {
    const onBeforeInstall = (e) => { e.preventDefault(); setInstallPrompt(e); };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    // Auto-open onboarding en primer visita móvil
    if (shouldShowXRInstallOnboarding()) {
      const t = setTimeout(() => setShowOnboarding(true), 700);
      return () => { clearTimeout(t); window.removeEventListener('beforeinstallprompt', onBeforeInstall); };
    }
    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, []);

  const promptInstall = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    setInstallPrompt(null);
  };

  const startScan = async () => {
    setScanError('');
    setScanning(true);
    await new Promise((r) => setTimeout(r, 100));
    if (!videoRef.current) return;
    try {
      const reader = new BrowserMultiFormatReader();
      readerRef.current = reader;
      await reader.decodeFromVideoDevice(undefined, videoRef.current, (result) => {
        if (!result) return;
        const text = String(result.getText() || '').trim();
        // Aceptar URL completa o solo token
        let token = null;
        try {
          const u = new URL(text);
          const m = u.pathname.match(/\/xr\/session\/([^/?#]+)/);
          if (m) token = m[1];
        } catch { /* not a URL */ }
        if (!token && /^[A-Za-z0-9_-]{16,}$/.test(text)) token = text;
        if (token) {
          stopScan();
          navigate(`/xr/session/${token}`);
        }
      });
    } catch (e) {
      setScanError(e?.message || 'No se pudo abrir la cámara');
      setScanning(false);
    }
  };

  const stopScan = () => {
    try {
      if (readerRef.current && typeof readerRef.current.reset === 'function') readerRef.current.reset();
      const stream = videoRef.current?.srcObject;
      if (stream && stream.getTracks) stream.getTracks().forEach((t) => t.stop());
    } catch { /* noop */ }
    readerRef.current = null;
    setScanning(false);
  };

  useEffect(() => () => stopScan(), []);

  const goManual = () => {
    if (!manualToken.trim()) return;
    navigate(`/xr/session/${manualToken.trim()}`);
  };

  const missingRequired = CAP_META.some((c) => c.required && caps[c.key] === false);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-cyan-900 text-slate-100" data-testid="xr-pwa-home">
      <XRInstallOnboarding open={showOnboarding} onClose={() => setShowOnboarding(false)} />
      <div className="max-w-lg mx-auto p-4 pb-24 space-y-4">
        {/* Header */}
        <header className="flex items-center justify-between pt-4">
          <div className="flex items-center gap-2">
            <Cube size={32} className="text-cyan-400" weight="fill" />
            <div>
              <div className="text-lg font-bold">SimuClinic XR</div>
              <div className="text-[10px] text-cyan-300/70 uppercase tracking-widest">PWA · Mobile / Tablet</div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {caps.is_online ? <CellSignalFull size={16} className="text-emerald-400" /> : <CellSignalSlash size={16} className="text-red-400" />}
            {caps.is_secure_context && <ShieldCheck size={16} className="text-cyan-300" title="Contexto seguro" />}
          </div>
        </header>

        {/* Install PWA CTA */}
        {installPrompt && (
          <Card className="bg-cyan-500/20 border-cyan-500">
            <CardContent className="p-3 flex items-center gap-3">
              <DownloadSimple size={24} className="text-cyan-300 shrink-0" weight="fill" />
              <div className="flex-1">
                <div className="text-sm font-semibold">Instala la app</div>
                <div className="text-[11px] text-cyan-100/80">Para mejor experiencia en pantalla completa.</div>
              </div>
              <Button size="sm" onClick={promptInstall} className="bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold" data-testid="install-pwa-btn">Instalar</Button>
            </CardContent>
          </Card>
        )}

        {caps.is_standalone && (
          <div className="text-[11px] text-emerald-300 flex items-center gap-1"><CheckCircle size={12} weight="fill" /> App instalada · modo standalone</div>
        )}

        {/* Install guide CTA — siempre disponible aunque no haya beforeinstallprompt (iOS) */}
        {!caps.is_standalone && (
          <button
            onClick={() => setShowOnboarding(true)}
            data-testid="open-install-guide-btn"
            className="w-full flex items-center justify-between p-3 rounded-lg bg-slate-800/40 border border-slate-700 hover:border-cyan-500 transition-all"
          >
            <div className="flex items-center gap-2 text-left">
              <DownloadSimple size={20} className="text-cyan-400" weight="fill" />
              <div>
                <div className="text-sm font-semibold text-white">Cómo instalar la app</div>
                <div className="text-[10px] text-slate-400">Guía paso a paso para iOS y Android</div>
              </div>
            </div>
            <span className="text-cyan-400 text-lg">›</span>
          </button>
        )}

        {/* Capabilities panel */}
        <Card className="bg-slate-800/60 border-slate-700">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] font-bold uppercase text-slate-400 tracking-widest">Capacidades del dispositivo</div>
              {caps.loading ? <span className="text-[10px] text-slate-500">Detectando…</span> : <Badge className="bg-slate-700 text-slate-300 text-[9px]">{caps.is_mobile ? 'Mobile' : 'Desktop'}</Badge>}
            </div>
            <div className="grid grid-cols-2 gap-1.5" data-testid="caps-list">
              {CAP_META.map((c) => (
                <div key={c.key} className={`flex items-center gap-1.5 text-[11px] p-1.5 rounded ${caps[c.key] ? 'bg-emerald-900/30' : (c.required ? 'bg-red-900/30' : 'bg-slate-700/30')}`} data-testid={`cap-${c.key}`}>
                  {caps[c.key] ? <CheckCircle size={11} weight="fill" className="text-emerald-400" /> : (c.required ? <WarningCircle size={11} weight="fill" className="text-red-400" /> : <span className="w-2.5 h-2.5 rounded-full bg-slate-500" />)}
                  <span className="truncate">{c.label}</span>
                </div>
              ))}
            </div>
            {missingRequired && (
              <div className="mt-3 text-[11px] text-red-300 flex items-start gap-1">
                <WarningCircle size={12} className="mt-0.5 shrink-0" />
                <span>Faltan capacidades requeridas. La cámara necesita HTTPS y permiso del usuario.</span>
              </div>
            )}
            {caps.webxr_supported && (
              <div className="mt-2 text-[11px] text-cyan-300 flex items-center gap-1">
                <CheckCircle size={11} weight="fill" /> WebXR AR disponible — modo inmersivo activable en Fase 2
              </div>
            )}
          </CardContent>
        </Card>

        {/* Scan QR */}
        <Card className="bg-slate-800/60 border-slate-700">
          <CardContent className="p-4">
            <div className="text-[10px] font-bold uppercase text-slate-400 tracking-widest mb-2">Vincular a una sesión</div>
            {!scanning ? (
              <Button onClick={startScan} disabled={!caps.camera_supported} className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-bold gap-2" data-testid="start-scan-btn">
                <QrCode size={16} weight="bold" /> Escanear QR
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="relative rounded-lg overflow-hidden border border-cyan-500/40 bg-black" data-testid="scan-view">
                  <video ref={videoRef} playsInline muted className="w-full aspect-square object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="w-48 h-48 border-2 border-cyan-400 rounded-lg" style={{ boxShadow: '0 0 30px rgba(6, 182, 212, 0.6)' }} />
                  </div>
                </div>
                <Button onClick={stopScan} variant="outline" className="w-full text-slate-900" data-testid="stop-scan-btn">Cancelar</Button>
              </div>
            )}
            {scanError && <div className="mt-2 text-[11px] text-red-300">{scanError}</div>}

            <div className="mt-4 pt-3 border-t border-slate-700/50">
              <div className="text-[10px] font-bold uppercase text-slate-400 tracking-widest mb-1.5">O ingresa el token manualmente</div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={manualToken}
                  onChange={(e) => setManualToken(e.target.value)}
                  placeholder="token…"
                  className="flex-1 px-3 py-2 rounded bg-slate-700/50 border border-slate-600 text-white text-sm font-mono"
                  data-testid="manual-token-input"
                />
                <Button onClick={goManual} disabled={!manualToken.trim()} className="bg-slate-700 hover:bg-slate-600" data-testid="manual-token-btn">Ir</Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info */}
        <div className="text-[10px] text-slate-500 text-center pt-4">
          <DeviceMobile size={12} className="inline mr-1" />
          Extensión oficial de SimuClinic · Backend común · Multi-tenant
        </div>
        <div className="text-center">
          <button onClick={() => navigate('/login')} className="text-[11px] text-cyan-400 hover:text-cyan-300 underline">
            ¿Instructor? Acceder a SimuClinic Web
          </button>
        </div>
      </div>
    </div>
  );
}
